import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useRouter } from "next/router";
import { useState } from "react";
import { toast } from "react-toastify";

import { getAdminInfo } from "../../core/admin.core";
import { posCashRegisterCore } from "../../core/cashRegister";
import { formatCurrency } from "../../core/currency.core";
import CashIcon from "../../icons/cash.icon";
import CloseIcon from "../../icons/close.icon";
import { CashRegisterType } from "../../types/cashRegister";
import { PosModal } from "../reuseables/modal.comp";
import ManualTransactionPanel from "./manualTrans.comp";
import { useTranslation } from "../../core/translation.core";
import LockOpenIcon from "../../icons/lockOpen.icon";

export default function ActiveRegister({ item }: { item: CashRegisterType }) {
  const queryClient = useQueryClient();
  const { t, msg } = useTranslation();
  const router = useRouter();
  let [isOpen, setIsOpen] = useState(false);
  const [closingSummary, setClosingSummary] = useState<string>("");
  const [closingInfo, setClosingInfo] = useState<CashRegisterType | null>(null);

  const { data } = useQuery({
    queryFn: () => posCashRegisterCore.listTransactions(item._id),
    queryKey: ["cash-register", item._id],
  });

  const { data: me } = useQuery({
    queryFn: () => getAdminInfo(),
    queryKey: ["admin"],
  });

  async function startClosing() {
    await posCashRegisterCore
      .startClosing({
        id: item._id,
      })
      .then((res: any) => {
        console.log(res);
        setClosingInfo(res.data.cashRegister);
        queryClient.invalidateQueries({ queryKey: ["cash-register"] });
      })
      .catch(() => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  async function close() {
    await posCashRegisterCore
      .close({
        closedBy: me.email,
        closingSummary,
        id: item._id,
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ["cash-register"] });
      })
      .catch(() => {
        toast.error(t(msg.errors.contactAdmin));
      });
  }

  return (
    <section className="bg-white dark:bg-her-darkblue2 space-y-6 px-1 py-5 lg:p-6 rounded-md shadow-md">
      <section className="grid grid-cols-1 md:grid-cols-[auto_40px] gap-y-3 md:gap-8 justify-between items-start">
        <div className="grid grid-cols-[150px_auto] gap-3">
          <h1 className="opacity-80">{t(msg.cash.openingCash)}: </h1>
          <p className="tracking-wide font-bold">{formatCurrency(item.openingCash, "HKD")}</p>

          <h1 className="opacity-80">{t(msg.cash.startDate)}: </h1>
          <p className="tracking-wide font-bold">{format(new Date(item.createdAt), "EEE, yyyy-MM-dd HH:mm:ss")}</p>

          {item.endAt && (
            <>
              <h1 className="opacity-80">{t(msg.cash.endDate)}: </h1>
              <p className="tracking-wide font-bold">{format(new Date(item.endAt), "EEE, yyyy-MM-dd HH:mm:ss")}</p>
            </>
          )}
        </div>

        <div className="w-fit capitalize text-green-600 text-sm lg:text-base flex gap-2 items-center px-2 py-1 rounded-md shadow-sm bg-[#def7de]">
          <LockOpenIcon />
        </div>
      </section>

      <div>
        {data?.transactions?.map(({ totalAmount, transactions, type }) => (
          <div key={type} className="space-y-3 py-4">
            <div className="flex justify-between border-b pb-1 font-bold text-lg">
              <h1 className="capitalize text-gray-400 text-[15px]">
                <span>{type} </span>
                <span>({transactions.length})</span>
              </h1>

              <p>
                {type === "withdrawal" ? (
                  <span className="!text-red-500"> - {formatCurrency(totalAmount, "HKD")}</span>
                ) : (
                  formatCurrency(totalAmount, "HKD")
                )}
              </p>
            </div>

            {transactions.map(({ amount, date, type, desc, orderId, orderNumber }) => (
              <div
                key={type}
                className="block space-y-2 lg:space-y-0 border-b pb-4 opacity-80 dark:opacity-100 lg:grid lg:grid-cols-3 justify-between gap-8"
              >
                <span className="tracking-wide">
                  {format(new Date(date), "yyyy-MM-dd")} {format(new Date(date), "HH:mm:ss")}
                </span>

                <div>
                  {type === "withdrawal" || type === "deposit" ? (
                    <span> {desc}</span>
                  ) : (
                    <button
                      onClick={() => router.push(`/new-order/checkout?orderId=${orderId}`)}
                      className="text-blue-500 underline"
                    >
                      {orderNumber}
                    </button>
                  )}
                </div>

                <p className="opacity-80 justify-self-end">
                  {type === "withdrawal" ? (
                    <span className="!text-red-500"> - {formatCurrency(amount, "HKD")}</span>
                  ) : (
                    formatCurrency(amount, "HKD")
                  )}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>

      <section>
        <div className="flex gap-4">
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 flex gap-2 items-center shadow-sm px-3 py-2 rounded-md text-white"
          >
            <CashIcon />
            {t(msg.cash.cashTransaction)}
          </button>

          {isOpen ? (
            <PosModal setModalOpened={setIsOpen}>
              <ManualTransactionPanel setIsOpen={setIsOpen} />
            </PosModal>
          ) : null}

          <button
            onClick={startClosing}
            className="w-fit capitalize font-medium bg-white text-blue-600 text-sm lg:text-base flex gap-2 items-center px-5 py-1 rounded-md  "
          >
            <CloseIcon />
            {t(msg.cash.startClosing)}
          </button>
        </div>

        <div className=" pt-8 space-y-16">
          {closingInfo && (
            <div className="pt-16 mx-auto w-fit py-4 px-8 text-center shadow-md">
              <section className="flex gap-4 lg:block space-y-0 lg:space-y-7">
                <div className="block space-y-5 lg:space-y-0 lg:grid lg:grid-cols-[150px_150px_150px_150px_150px_150px] gap-12 font-medium">
                  <h1>{t(msg.cash.totalCashReceived)}</h1>
                  <h1>{t(msg.cash.totalSales)}</h1>
                  <h1 className="text-green-500">{t(msg.cash.totalDeposit)}</h1>
                  <h1 className="text-red-500">{t(msg.cash.totalWithdraw)}</h1>
                  <h1>{t(msg.cash.totalRefund)}</h1>
                  <h1>{t(msg.cash.totalTransactions)}</h1>
                </div>

                <div className="block space-y-5 lg:space-y-0 tracking-wide font-bold lg:grid lg:grid-cols-[150px_150px_150px_150px_150px_150px] gap-12 ">
                  <h1>{formatCurrency(closingInfo.totalCashReceived, "HKD")}</h1>
                  <h1>{formatCurrency(closingInfo.totalSales, "HKD")}</h1>
                  <h1 className="text-green-500">
                    {" "}
                    {formatCurrency(closingInfo.totalDeposit, "HKD")}
                  </h1>
                  <h1 className="text-red-500">
                    {" "}
                    - {formatCurrency(closingInfo.totalWithdraw, "HKD")}
                  </h1>
                  <h1>{formatCurrency(closingInfo.totalRefund, "HKD")}</h1>
                  <h1>{closingInfo.transactions.filter((transaction) => transaction.type !== "withdrawal").length}</h1>
                </div>
              </section>
            </div>
          )}

          {/* close register */}
          {closingInfo && (
            <div className="space-y-5">
              <div className="space-y-4">
                <h1>{t(msg.cash.enterClosingSummary)}</h1>
                <textarea
                  value={closingSummary}
                  onChange={(e) => setClosingSummary(e.target.value)}
                  className="p-2 text-black outline-none rounded-md shadow-md"
                />
              </div>

              <div className="flex items-center gap-5">
                <button onClick={startClosing} className="bg-green-600 w-fit px-4 py-1 rounded-md shadow-sm">
                  {t(msg.cash.refetchTransactions)}
                </button>

                <button onClick={close} className="bg-red-600 w-fit px-4 py-1 rounded-md shadow-sm">
                  {t(msg.cash.closeRegister)}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </section>
  );
}
